import React, { useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

import { EditStepsDialog } from './EditStepsDialog'
import { EdgeEditDialog } from './EdgeEditDialog'
import { StepEditDialog } from './StepEditDialog'
import { PipelineFlowChart } from './PipelineFlowChart'
import {
    PipelineBuilderScope,
    usePipelineBuilderState,
} from './usePipelineBuilderState'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 400,
        gap: theme.spacing(1.5),
        width: '100%',
    },
    toolbar: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexShrink: 0,
    },
    flowWrap: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        width: '100%',
    },
    loading: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 280,
    },
    lockedNote: {
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
    },
}))

interface PipelineBuilderTabProps {
    scope?: 'property' | 'org'
    apartmentId?: number
    organizationId?: number | null
    configId: number
    readOnly?: boolean
    fullWidth?: boolean
}

export const PipelineBuilderTab: React.FC<PipelineBuilderTabProps> = ({
    scope = 'property',
    apartmentId,
    organizationId,
    configId,
    readOnly = false,
    fullWidth = false,
}) => {
    const classes = useStyles()
    const [editStepsOpen, setEditStepsOpen] = useState(false)

    const scopeConfig: PipelineBuilderScope =
        scope === 'org'
            ? { scope: 'org', organizationId: organizationId ?? null }
            : { scope: 'property', apartmentId: apartmentId ?? null }

    const {
        configName,
        definition,
        orderedSteps,
        enabledSteps,
        transitions,
        editTarget,
        editingStep,
        editingTransition,
        editingEdgeSteps,
        isLoading,
        isEditable,
        previousConfigOptions,
        removeStep,
        updateStep,
        applyStepsEdit,
        updateTransition,
        fetchConfigDefinition,
        openNode,
        openEdge,
        closeEditor,
    } = usePipelineBuilderState(scopeConfig, configId, readOnly)

    const effectiveReadOnly = readOnly || !isEditable
    const builderApartmentId = scope === 'property' ? apartmentId : undefined

    if (isLoading) {
        return (
            <Box className={classes.loading}>
                <CircularProgress size={28} />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.toolbar}>
                {configName && (
                    <Typography variant="subtitle1">{configName}</Typography>
                )}
                {effectiveReadOnly && scope === 'property' && (
                    <Typography className={classes.lockedNote}>
                        Read-only — linked to an active leasing season.
                    </Typography>
                )}
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setEditStepsOpen(true)}
                    disabled={effectiveReadOnly}
                >
                    Edit steps
                </Button>
            </Box>

            <Box className={classes.flowWrap}>
                <PipelineFlowChart
                    enabledSteps={enabledSteps}
                    transitions={transitions}
                    editTarget={editTarget}
                    onNodeClick={openNode}
                    onEdgeClick={openEdge}
                    onEditSteps={() => setEditStepsOpen(true)}
                    readOnly={effectiveReadOnly}
                    fullWidth={fullWidth}
                />
            </Box>

            <EditStepsDialog
                open={editStepsOpen}
                enabledSteps={enabledSteps}
                orderedSteps={orderedSteps}
                readOnly={effectiveReadOnly}
                apartmentId={builderApartmentId}
                previousConfigOptions={previousConfigOptions}
                onFetchConfigDefinition={fetchConfigDefinition}
                onClose={() => setEditStepsOpen(false)}
                onApply={applyStepsEdit}
            />

            <StepEditDialog
                open={editTarget?.kind === 'node'}
                step={editingStep}
                definition={definition}
                apartmentId={builderApartmentId}
                readOnly={effectiveReadOnly}
                onClose={closeEditor}
                onSave={updateStep}
                onRemove={removeStep}
            />

            <EdgeEditDialog
                open={editTarget?.kind === 'edge'}
                fromStep={editingEdgeSteps?.fromStep ?? null}
                toStep={editingEdgeSteps?.toStep ?? null}
                transition={editingTransition}
                readOnly={effectiveReadOnly}
                onClose={closeEditor}
                onSave={updateTransition}
            />
        </Box>
    )
}
